// Inbox actions controller - registers keyboard shortcuts for actions on the
// currently focused inbox entry. Listens for inbox-focus:changed events to
// know which entry is active, then clicks the matching button/link directly
// so Turbo handles navigation properly.
//
// Mount on the same element as inbox-focus controller.
//
// Buttons are identified by data-shortcut="<name>" attributes on the actual
// UI::LinkButton elements in each inbox entry card.
//
// Shortcut keys:
//   s - Snooze
//   u - Unsnooze
//   e - Archive (conversation leaves the inbox until its next message)
//   c - Close
//   p - Remind Prospect / Remind Prospect to Verify
//   a - Remind Advocate / Activate conversation / Approve application/activity /
//       Approve all tag suggestions / Snooze: This Afternoon (when snooze panel open)
//   r - Reject application/activity
//   o - Open / View full item
//   a/t/d/m/n/w - Snooze quick options (This Afternoon / Tomorrow / 2 Days / On Monday / Next week / 2 Weeks).
//                Letter-based to avoid conflicting with 1/2 tab shortcuts.
//   Escape - Cancel confirmation

import { Controller } from "@hotwired/stimulus"

const ACTION_SHORTCUTS = Object.freeze([
  { key: "s", actions: ["snooze"], description: "Snooze" },
  { key: "u", actions: ["unsnooze"], description: "Unsnooze" },
  { key: "e", actions: ["archive"], description: "Archive" },
  { key: "c", actions: ["close"], description: "Close entry" },
  { key: "p", actions: ["remind-prospect", "remind-prospect-to-verify"], description: "Remind Prospect" },
  { key: "a", actions: ["remind-advocate", "activate", "approve", "approve-all", "snooze-this-afternoon"], description: "Remind Advocate / Activate / Approve / Snooze: This Afternoon" },
  { key: "r", actions: ["reject"], description: "Reject application/activity" },
  { key: "o", actions: ["open"], description: "Open full item" },
  { key: "t", actions: ["snooze-tomorrow"], description: "Snooze: Tomorrow" },
  { key: "d", actions: ["snooze-two-days", "download"], description: "Snooze: Two days / Download data" },
  { key: "m", actions: ["snooze-on-monday"], description: "Snooze: On Monday" },
  { key: "n", actions: ["snooze-next-week"], description: "Snooze: Next week" },
  { key: "w", actions: ["snooze-two-weeks"], description: "Snooze: Two weeks" },
  { key: "Escape", actions: ["cancel"], description: "Cancel" },
])

export default class extends Controller {
  connect() {
    this.focusedEntry = null
    this.registerShortcuts()
  }

  disconnect() {
    this.deregisterShortcuts()
  }

  onFocusChanged(event) {
    this.focusedEntry = event.detail?.entry || null
  }

  // Private

  registerShortcuts() {
    this.registeredKeys = ACTION_SHORTCUTS.map(s => s.key)

    ACTION_SHORTCUTS.forEach(({ key, actions, description }) => {
      document.body.dispatchEvent(
        new CustomEvent("shortcut:register", {
          detail: {
            key,
            description,
            category: "Inbox",
            handler: () => this.executeAction(actions),
          },
          bubbles: true,
        })
      )
    })
  }

  deregisterShortcuts() {
    (this.registeredKeys || []).forEach((key) => {
      document.body.dispatchEvent(
        new CustomEvent("shortcut:deregister", { detail: { key }, bubbles: true })
      )
    })
  }

  executeAction(actions) {
    if (!this.focusedEntry) return

    for (const action of actions) {
      const el = this.focusedEntry.querySelector(`[data-shortcut="${action}"]`)
      if (el) {
        el.click()
        return
      }
    }
  }
}
